import { useEffect } from 'react';
import { trackEvent } from '@/monitoring/ga4';

/**
 * document-level 클릭 이벤트 위임으로 `data-track` 속성이 있는 요소의 클릭을 GA4에 전송한다.
 *
 * 사용법: 추적하고 싶은 요소에 data-track="sidebar_agents" 속성을 추가한다.
 * 선택적으로 data-track-* 속성으로 추가 매개변수를 전달할 수 있다.
 *   예: <button data-track="plan_select" data-track-plan="pro" data-track-price="29">
 */
export function useClickTracking(): void {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-track]');
      if (!target) return;

      const trackName = target.dataset.track;
      if (!trackName) return;

      const params: Record<string, string> = { click_target: trackName };

      for (const [key, value] of Object.entries(target.dataset)) {
        if (key === 'track' || !key.startsWith('track') || value === undefined) continue;
        const paramKey = key
          .slice('track'.length)
          .replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`)
          .replace(/^_/, '');
        if (paramKey) params[paramKey] = value;
      }

      trackEvent('click', params);
    };

    document.addEventListener('click', handler, { capture: true, passive: true });
    return () => document.removeEventListener('click', handler, true);
  }, []);
}
